// Plot Presets - Pre-configured analysis views for Quick Analysis
// Enables users to explore cell data with meaningful questions and guided insights

import { effect, signal } from '@preact/signals'
import { safeLocal } from '../utils/safeStorage.js'

// ====== PLOT MODE ======
// 'user' = Simplified interface with essential options only
// 'expert' = Full access to all KPIs and advanced options
export const plotMode = signal(
  safeLocal.get('batemo:plotMode') || 'user'
)

// Persist plot mode
effect(() => {
  safeLocal.set('batemo:plotMode', plotMode.value)
})

// ====== ACTIVE PRESET ======
// null = Custom/manual configuration
// string = Active preset ID
// Default to Performance Analysis (gravimetric) for new users
export const activePreset = signal(
  safeLocal.get('batemo:activePreset') || 'perf-gravimetric'
)

// Persist active preset
effect(() => {
  if (activePreset.value) {
    safeLocal.set('batemo:activePreset', activePreset.value)
  } else {
    safeLocal.remove('batemo:activePreset')
  }
})

// ====== URL SYNC ======
let _syncingFromURL = false

/**
 * Read preset ID from URL query parameter and apply it.
 * Returns the preset ID if valid, null otherwise.
 */
export function readPresetFromURL() {
  const url = new URL(window.location.href)
  const presetParam = url.searchParams.get('preset')
  if (presetParam && PLOT_PRESETS[presetParam]) {
    _syncingFromURL = true
    activePreset.value = presetParam
    _syncingFromURL = false
    return presetParam
  }
  return null
}

// Write activePreset to URL (replaceState, no history entry)
effect(() => {
  if (_syncingFromURL) return
  const url = new URL(window.location.href)
  const isGraphView = url.pathname === '/explorer' && url.searchParams.get('view') === 'graph'
  if (!isGraphView) return

  const currentUrlPreset = url.searchParams.get('preset')
  if (activePreset.value === currentUrlPreset) return

  if (activePreset.value) {
    url.searchParams.set('preset', activePreset.value)
  } else {
    url.searchParams.delete('preset')
  }
  window.history.replaceState({}, '', url.toString())
})

// ====== CURRENT REGRESSION DATA ======
// Stores the latest regression calculation from FreePlotECharts
// Used to generate insights for evolution presets
export const currentRegression = signal(null)

// ====== CURRENT CLUSTER DATA ======
// Stores bounding box data for each category
// Used to generate insights for cluster-based presets
export const currentClusterData = signal(null)

// ====== SHOW INSIGHTS TOGGLE ======
// Controls whether insights are displayed in the Quick Analysis section
export const showInsights = signal(
  safeLocal.get('batemo:showInsights') !== 'false' // true by default
)

// Persist show insights setting
effect(() => {
  safeLocal.set('batemo:showInsights', showInsights.value)
})

// ====== PRESET CATEGORIES ======
// Icons are string references to components from Icons.jsx
export const PRESET_CATEGORIES = [
  {
    id: 'performance',
    label: 'Performance Analysis',
    icon: 'IconZap',
    description: 'Compare energy and power density across cells'
  },
  {
    id: 'chemistry',
    label: 'Chemistry Analysis',
    icon: 'IconFlask',
    description: 'Explore how different chemistries affect performance'
  },
  {
    id: 'technology',
    label: 'Technology Analysis',
    icon: 'IconCpu',
    description: 'Compare different battery technologies'
  },
  {
    id: 'supplier',
    label: 'Supplier Analysis',
    icon: 'IconFactory',
    description: 'Analyze performance by manufacturer'
  },
  {
    id: 'evolution',
    label: 'Market Evolution',
    icon: 'IconTrendingUp',
    description: 'Track how battery technology evolves over time'
  }
]

// ====== PRESET DEFINITIONS ======
// Each preset contains:
// - question: The analysis question being answered
// - explanation: Guidance on what to look for
// - xAxis, yAxis: KPI IDs from AVAILABLE_KPIS
// - colorBy: Category for color coding
// - filters: Optional filters to apply (format, chemistry, etc.)
// - requiredBasis: Optional - forces basis to this value
// - requiredDomain: Optional - forces domain to this value

export const PLOT_PRESETS = {
  // ========== PERFORMANCE ANALYSIS ==========
  'perf-gravimetric': {
    category: 'performance',
    question: 'How do cells compare in gravimetric energy and power density?',
    explanation: 'Compare cells by their gravimetric energy density (Wh/kg) vs. gravimetric peak power density (W/kg). Cells colored by format. Look for trade-offs between energy and power—cells optimized for range vs. fast charging appear at opposite ends.',
    interpretation: 'Pouch cells and cells with cylindrical shape appear superior on cell level. However: Advantages of prismatic cells might appear on pack level.',
    xAxis: 'power_peak_grav',
    yAxis: 'energy_c10_grav',
    colorBy: 'format',
    filters: {},
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  'perf-volumetric': {
    category: 'performance',
    question: 'How do cells compare in volumetric energy and power density?',
    explanation: 'Compare cells by their volumetric energy density (Wh/L) vs. volumetric peak power density (W/L). Cells colored by format. Volumetric performance is critical for space-constrained applications like passenger EVs.',
    interpretation: 'Pouch cells and cells with cylindrical shape appear superior on cell level. However: Advantages of prismatic cells might appear on pack level.',
    xAxis: 'power_peak_vol',
    yAxis: 'energy_c10_vol',
    colorBy: 'format',
    filters: {},
    requiredBasis: 'vol',
    requiredDomain: 'energy-power'
  },

  // ========== CHEMISTRY ANALYSIS ==========
  'chem-anode-performance': {
    category: 'chemistry',
    question: 'Which anode chemistry is used for which performance requirement?',
    explanation: 'Different anode chemistries are shown in different colors. To avoid format-dependent interdependencies, only cylindrical cells are displayed. Cells with unknown chemistry (analysis ongoing) are excluded.',
    interpretation: 'Graphite-silicon blend cells lead in energy / C/10 / gravimetric performance, however there is an overlapping area due to varying content of silicon.',
    xAxis: 'power_peak_grav',
    yAxis: 'energy_c10_grav',
    colorBy: 'anode-chemistry',
    filters: {
      shape: ['cylindrical'],
      anodeChemistry: ['graphite', 'graphite-silicon blend', 'LTO']
    },
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  'chem-cathode-performance': {
    category: 'chemistry',
    question: 'Which cathode chemistry is used for which performance requirement?',
    explanation: 'Different cathode chemistries are shown in different colors. To avoid format-dependent interdependencies, only cylindrical cells are displayed. Cells with unknown chemistry (analysis ongoing) are excluded.',
    interpretation: 'On average, LFP shows the lowest energy densities. Low-nickel NMC show a lower energy density than high-nickel NMC. High-nickel NMC and NCA have an overlap with high-nickel NMC dominating at very large energy densities.',
    xAxis: 'power_peak_grav',
    yAxis: 'energy_c10_grav',
    colorBy: 'cathode-chemistry',
    filters: {
      shape: ['cylindrical'],
      cathodeChemistry: ['LCO', 'LFP', 'LMO', 'NCA', 'NMC (high-nickel)', 'NMC (low-nickel)', 'NMX']
    },
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  'chem-energy-capacity': {
    category: 'chemistry',
    question: 'How does the cell chemistry affect energy vs. capacity?',
    explanation: 'Compare how different cell chemistries achieve their energy density through capacity or voltage. Cells with higher voltage but similar capacity will appear higher on the energy axis. Cells with unknown chemistry are excluded.',
    interpretation: 'On average, NMC and NCA chemistries are indistinguishable. LFP cells have a slightly worse capacity to energy ratio. The worst ratio can be observed for LTO cells. However note, that the latter chemistries have advantages in cost and safety.',
    xAxis: 'capacity_c10_grav',
    yAxis: 'energy_c10_grav',
    colorBy: 'main-chemistry',
    excludeUnknownChemistry: true,
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  // ========== TECHNOLOGY ANALYSIS ==========
  'tech-na-vs-li': {
    category: 'technology',
    question: 'How does the performance of Na-Ion cells compare to Li-Ion cells?',
    explanation: 'Different technologies are shown in different colors. To study the basic performance of Na-Ion regardless of format, cells of all formats are displayed. Observe where Na-Ion technology currently sits relative to established Li-Ion.',
    interpretation: 'Na-Ion cells are currently located in the low energy and low power region compared to Li-Ion cells.',
    xAxis: 'power_peak_grav',
    yAxis: 'energy_c10_grav',
    colorBy: 'technology',
    filters: {},
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  'tech-format-comparison': {
    category: 'technology',
    question: 'How do different cell formats compare in energy and power?',
    explanation: 'Cell formats are shown in different colors. Cylindrical cells tend to cluster differently than pouch or prismatic. Look for format-specific advantages in energy or power density.',
    interpretation: 'Pouch cells and cells with cylindrical shape appear superior on cell level. However: Advantages of prismatic cells might appear on pack level.',
    xAxis: 'power_peak_grav',
    yAxis: 'energy_c10_grav',
    colorBy: 'format',
    filters: {},
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  'tech-volumetric-comparison': {
    category: 'technology',
    question: 'Which formats achieve the best volumetric performance?',
    explanation: 'Compare volumetric energy and power density across formats. Prismatic and pouch cells may show different volumetric characteristics than cylindrical cells.',
    interpretation: 'Pouch cells and cells with cylindrical shape appear superior on cell level. However: Advantages of prismatic cells might appear on pack level.',
    xAxis: 'power_peak_vol',
    yAxis: 'energy_c10_vol',
    colorBy: 'format',
    filters: {},
    requiredBasis: 'vol',
    requiredDomain: 'energy-power'
  },

  // ========== SUPPLIER ANALYSIS ==========
  'supplier-lto': {
    category: 'supplier',
    question: 'Which supplier delivers LTO cells?',
    explanation: 'Different suppliers are shown in different colors. Only cells with an LTO anode are displayed. Identify which manufacturers focus on the LTO segment and how their products compare.',
    interpretation: 'Different suppliers position themselves in different regions of the market.',
    xAxis: 'power_peak_grav',
    yAxis: 'energy_c10_grav',
    colorBy: 'manufacturer',
    filters: {
      anodeChemistry: ['LTO']
    },
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  'supplier-lfp': {
    category: 'supplier',
    question: 'Which supplier delivers LFP cells?',
    explanation: 'Different suppliers are shown in different colors. Only cells with an LFP cathode are displayed. See which manufacturers compete in the LFP market and their product positioning.',
    interpretation: 'Different suppliers position themselves in different regions of the market.',
    xAxis: 'power_peak_grav',
    yAxis: 'energy_c10_grav',
    colorBy: 'manufacturer',
    filters: {
      cathodeChemistry: ['LFP']
    },
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  'supplier-high-power-21700': {
    category: 'supplier',
    question: 'Which suppliers deliver high-power 21700 cells (>2 kW/kg)?',
    explanation: 'Different suppliers are shown in different colors. Only 21700 cells with gravimetric power density above 2000 W/kg are displayed. Identify the leaders in high-power cylindrical cells.',
    interpretation: 'Only few suppliers serve the area of high power 21700 cells.',
    xAxis: 'power_peak_grav',
    yAxis: 'energy_c10_grav',
    colorBy: 'manufacturer',
    filters: {
      format: ['21700'],
      minPowerGrav: 2000
    },
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  'supplier-overview': {
    category: 'supplier',
    question: 'How do different manufacturers position their products?',
    explanation: 'All cells colored by manufacturer. Observe how each company positions their portfolio across the energy-power spectrum. Some focus on high-energy, others on high-power applications.',
    interpretation: 'Different suppliers position themselves in different regions of the market.',
    xAxis: 'power_peak_grav',
    yAxis: 'energy_c10_grav',
    colorBy: 'manufacturer',
    filters: {},
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  // ========== MARKET EVOLUTION ==========
  'evolution-energy': {
    category: 'evolution',
    question: 'How does available energy density evolve over time?',
    explanation: 'Energy density is shown over time, with different cathode chemistries in different colors. Check how energy density has improved year over year and how the occurrence of different cathode chemistries evolves.',
    xAxis: 'year',
    yAxis: 'energy_c10_grav',
    colorBy: 'cathode-chemistry',
    filters: {},
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  'evolution-power': {
    category: 'evolution',
    question: 'How does available power density evolve over time?',
    explanation: 'Power density is shown over time, with different cathode chemistries in different colors. Track how manufacturers have pushed power density limits and which chemistries lead.',
    xAxis: 'year',
    yAxis: 'power_peak_grav',
    colorBy: 'cathode-chemistry',
    filters: {},
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  'evolution-capacity-fade': {
    category: 'evolution',
    question: 'How does post-formation capacity loss evolve over time?',
    explanation: 'Post-formation capacity loss is shown over time, with different anode chemistries in different colors. Check which chemistries are more prone to high post-formation capacity loss and how that trend evolves.',
    xAxis: 'year',
    yAxis: 'aging_cap_loss',
    colorBy: 'anode-chemistry',
    filters: {},
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  },

  'evolution-quality': {
    category: 'evolution',
    question: 'How does capacity variation evolve over time?',
    explanation: 'Cell-to-cell capacity variation is shown over time, with different anode chemistries highlighted. Lower values indicate better manufacturing consistency.',
    xAxis: 'year',
    yAxis: 'batch_cap_variation',
    colorBy: 'anode-chemistry',
    filters: {},
    requiredBasis: 'grav',
    requiredDomain: 'energy-power'
  }
}

// ====== HELPER FUNCTIONS ======

/**
 * Get all presets for a specific category
 */
export function getPresetsByCategory(categoryId) {
  return Object.entries(PLOT_PRESETS)
    .filter(([_, preset]) => preset.category === categoryId)
    .map(([id, preset]) => ({ id, ...preset }))
}

/**
 * Get a preset by its ID
 */
export function getPreset(presetId) {
  return PLOT_PRESETS[presetId] || null
}

/**
 * Get all presets as an array with IDs
 */
export function getAllPresets() {
  return Object.entries(PLOT_PRESETS).map(([id, preset]) => ({ id, ...preset }))
}

// ====== USER MODE KPI OPTIONS ======
// Simplified KPI list for user mode (essential metrics only)
export const USER_MODE_KPIS = {
  'energy-power': {
    grav: [
      { id: 'energy_c10_grav', label: 'Energy Density', unit: 'Wh/kg' },
      { id: 'power_peak_grav', label: 'Peak Power Density', unit: 'W/kg' },
      { id: 'power_cont_grav', label: 'Continuous Power Density', unit: 'W/kg' },
    ],
    vol: [
      { id: 'energy_c10_vol', label: 'Energy Density', unit: 'Wh/L' },
      { id: 'power_peak_vol', label: 'Peak Power Density', unit: 'W/L' },
      { id: 'power_cont_vol', label: 'Continuous Power Density', unit: 'W/L' },
    ],
    abs: [
      { id: 'energy_c10_abs', label: 'Energy', unit: 'Wh' },
      { id: 'power_peak_abs', label: 'Peak Power', unit: 'W' },
      { id: 'power_cont_abs', label: 'Continuous Power', unit: 'W' },
    ]
  },
  'capacity-current': {
    grav: [
      { id: 'capacity_c10_grav', label: 'Capacity Density', unit: 'Ah/kg' },
      { id: 'current_peak_grav', label: 'Peak Current Density', unit: 'A/kg' },
      { id: 'current_cont_grav', label: 'Continuous Current Density', unit: 'A/kg' },
    ],
    vol: [
      { id: 'capacity_c10_vol', label: 'Capacity Density', unit: 'Ah/L' },
      { id: 'current_peak_vol', label: 'Peak Current Density', unit: 'A/L' },
      { id: 'current_cont_vol', label: 'Continuous Current Density', unit: 'A/L' },
    ],
    abs: [
      { id: 'capacity_c10_abs', label: 'Capacity', unit: 'Ah' },
      { id: 'current_peak_abs', label: 'Peak Current', unit: 'A' },
      { id: 'current_cont_abs', label: 'Continuous Current', unit: 'A' },
    ]
  },
  // Common KPIs available in all modes
  common: [
    { id: 'year', label: 'Year', unit: '' },
    { id: 'mass', label: 'Cell Mass', unit: 'g' },
    { id: 'volume', label: 'Cell Volume', unit: 'cm³' },
  ]
}

/**
 * Get filtered KPIs for user mode based on current basis and domain
 */
export function getUserModeKPIs(domain, basis) {
  const domainKPIs = USER_MODE_KPIS[domain]?.[basis] || []
  const commonKPIs = USER_MODE_KPIS.common || []
  return [...domainKPIs, ...commonKPIs]
}

// ====== INSIGHT GENERATION ======
// Templates for generating insights based on regression data

/**
 * Insight templates for evolution presets
 * {change} = percentage change per year (e.g., "+2.3%")
 * {direction} = "increased" or "decreased"
 * {metric} = the metric name (e.g., "energy density")
 * {strength} = "slightly", "moderately", "significantly"
 * {confidence} = based on R² value
 */
const INSIGHT_TEMPLATES = {
  'evolution-energy': {
    positive: 'Energy density has {strength} improved, increasing by {change} per year on average.',
    negative: 'Energy density has {strength} declined, decreasing by {change} per year on average.',
    neutral: 'Energy density has remained relatively stable over the observed period.',
    lowData: 'Not enough data to reliably assess energy density trends.',
    metric: 'energy density',
    positiveIsGood: true
  },
  'evolution-power': {
    positive: 'Power density has {strength} improved, increasing by {change} per year on average.',
    negative: 'Power density has {strength} declined, decreasing by {change} per year on average.',
    neutral: 'Power density has remained relatively stable over the observed period.',
    lowData: 'Not enough data to reliably assess power density trends.',
    metric: 'power density',
    positiveIsGood: true
  },
  'evolution-capacity-fade': {
    positive: 'Post-formation capacity loss has {strength} worsened, increasing by {change} per year.',
    negative: 'Post-formation capacity loss has {strength} improved, decreasing by {change} per year.',
    neutral: 'Post-formation capacity loss has remained relatively stable.',
    lowData: 'Not enough data to reliably assess capacity loss trends.',
    metric: 'capacity loss',
    positiveIsGood: false  // Lower is better
  },
  'evolution-quality': {
    positive: 'Cell-to-cell capacity variation is increasing by {change} per year.',
    negative: 'Cell-to-cell capacity variation is decreasing by {change} per year.',
    neutral: 'Cell-to-cell capacity variation has remained relatively stable.',
    lowData: 'Not enough data to reliably assess capacity variation trends.',
    metric: 'manufacturing quality',
    positiveIsGood: false  // Lower variation is better
  }
}

/**
 * Check whether a regression has enough data quality to be trustworthy.
 * Used by both trendline rendering and insight generation.
 * @param {object} regression - { slope, intercept, r2, minX, maxX, n }
 * @returns {boolean}
 */
export function isRegressionReliable(regression) {
  if (!regression) return false
  if (regression.n < 20) return false
  if (regression.r2 < 0.02) return false
  if ((regression.maxX - regression.minX) < 2) return false
  return true
}

/**
 * Generate an insight based on regression data and preset
 * @param {string} presetId - The active preset ID
 * @param {object} regression - { slope, intercept, r2, minX, maxX, n }
 * @returns {object} { insight: string, confidence: string, icon: string }
 */
export function generateInsight(presetId, regression) {
  if (!regression || !presetId) return null

  const template = INSIGHT_TEMPLATES[presetId]
  if (!template) return null

  // Reliability check — return fallback insight when data is insufficient
  if (!isRegressionReliable(regression)) {
    let fallbackText
    if (regression.n < 20) {
      fallbackText = template.lowData || 'Not enough data points for a reliable trend analysis.'
    } else if ((regression.maxX - regression.minX) < 2) {
      fallbackText = 'Time range too short to determine a meaningful trend.'
    } else {
      fallbackText = 'No clear trend can be identified in the available data.'
    }

    return {
      insight: fallbackText,
      confidence: '',
      iconName: 'minus',
      isFallback: true,
      isPositiveNews: false,
      percentChange: '0',
      r2: regression.r2.toFixed(2)
    }
  }

  const { slope, r2 } = regression

  // Calculate percentage change
  const avgY = (regression.slope * (regression.minX + regression.maxX) / 2) + regression.intercept
  const percentChange = avgY > 0 ? Math.abs((slope / avgY) * 100) : 0
  const changeStr = percentChange.toFixed(1) + '%'

  // Determine strength based on percentage change
  let strength
  if (percentChange < 1) strength = 'slightly'
  else if (percentChange < 3) strength = 'moderately'
  else strength = 'significantly'

  // Determine confidence based on R²
  let confidence
  if (r2 >= 0.5) confidence = 'High confidence'
  else if (r2 >= 0.2) confidence = 'Moderate confidence'
  else confidence = ''

  // Determine direction and select template
  let insightText
  let iconName

  if (Math.abs(percentChange) < 0.5) {
    // Neutral - no significant change
    insightText = template.neutral
    iconName = 'minus'
  } else if (slope > 0) {
    // Positive slope
    insightText = template.positive.replace('{strength}', strength).replace('{change}', changeStr)
    iconName = template.positiveIsGood ? 'trendingUp' : 'trendingDown'
  } else {
    // Negative slope
    insightText = template.negative.replace('{strength}', strength).replace('{change}', changeStr)
    iconName = template.positiveIsGood ? 'trendingDown' : 'trendingUp'
  }

  // Determine if this is good or bad news
  const isPositiveNews = (slope > 0 && template.positiveIsGood) || (slope < 0 && !template.positiveIsGood)

  return {
    insight: insightText,
    confidence,
    iconName,
    isPositiveNews,
    isFallback: false,
    percentChange: percentChange.toFixed(1),
    r2: r2.toFixed(2)
  }
}

// ====== CLUSTER INSIGHT GENERATION ======

/**
 * Generate insights based on cluster/bounding box data
 * Analyzes which categories dominate which performance regions
 * @param {string} presetId - The active preset ID
 * @param {object} clusterData - { categories: [{ name, minX, maxX, minY, maxY, count, avgX, avgY }] }
 * @param {object} kpiInfo - { xLabel, yLabel, xUnit, yUnit }
 * @returns {object} { insight: string, details: string[], icon: string }
 */
export function generateClusterInsight(presetId, clusterData, kpiInfo) {
  if (!clusterData || !clusterData.categories || clusterData.categories.length < 2) return null

  const preset = PLOT_PRESETS[presetId]
  if (!preset) return null

  const cats = clusterData.categories

  // Sort by average Y value (typically energy density)
  const sortedByY = [...cats].sort((a, b) => b.avgY - a.avgY)
  const topPerformer = sortedByY[0]
  const bottomPerformer = sortedByY[sortedByY.length - 1]

  // Sort by average X value (typically power density)
  const sortedByX = [...cats].sort((a, b) => b.avgX - a.avgX)
  const highXPerformer = sortedByX[0]

  // Calculate overlap between top categories
  const overlapInfo = calculateOverlap(sortedByY[0], sortedByY[1])

  // Generate insight based on preset type
  let insight = ''
  let details = []
  let iconName = 'flask' // default

  if (preset.colorBy === 'cathode-chemistry' || preset.colorBy === 'anode-chemistry') {
    // Chemistry comparison
    insight = `${topPerformer.name} cells lead in ${kpiInfo.yLabel?.toLowerCase() || 'Y-axis'} performance, with an average of ${formatInsightValue(topPerformer.avgY, kpiInfo.yUnit)}.`

    if (topPerformer.name !== highXPerformer.name) {
      details.push(`${highXPerformer.name} leads in ${kpiInfo.xLabel?.toLowerCase() || 'X-axis'} (avg: ${formatInsightValue(highXPerformer.avgX, kpiInfo.xUnit)})`)
    }

    if (overlapInfo.hasSignificantOverlap) {
      details.push(`${sortedByY[0].name} and ${sortedByY[1].name} show significant overlap in performance`)
    }

    iconName = 'flask'

  } else if (preset.colorBy === 'format') {
    // Format comparison
    insight = `${topPerformer.name} format achieves the highest average ${kpiInfo.yLabel?.toLowerCase() || 'Y-axis'} at ${formatInsightValue(topPerformer.avgY, kpiInfo.yUnit)}.`

    details.push(`${bottomPerformer.name} format averages ${formatInsightValue(bottomPerformer.avgY, kpiInfo.yUnit)}`)

    iconName = 'cpu'

  } else if (preset.colorBy === 'manufacturer') {
    // Supplier comparison
    const topThree = sortedByY.slice(0, 3).map(c => c.name)
    insight = `Top performers in ${kpiInfo.yLabel?.toLowerCase() || 'Y-axis'}: ${topThree.join(', ')}.`

    details.push(`${topPerformer.name} leads with avg: ${formatInsightValue(topPerformer.avgY, kpiInfo.yUnit)}`)

    iconName = 'factory'

  } else if (preset.colorBy === 'technology') {
    // Technology comparison (Na-Ion vs Li-Ion)
    insight = `${topPerformer.name} technology leads in ${kpiInfo.yLabel?.toLowerCase() || 'Y-axis'} with ${formatInsightValue(topPerformer.avgY, kpiInfo.yUnit)} on average.`

    const gap = ((topPerformer.avgY - bottomPerformer.avgY) / bottomPerformer.avgY * 100).toFixed(0)
    if (gap > 10) {
      details.push(`${gap}% gap between ${topPerformer.name} and ${bottomPerformer.name}`)
    }

    iconName = 'bolt'
  }

  return {
    insight,
    details,
    iconName,
    topPerformer: topPerformer.name,
    categoryCount: cats.length
  }
}

/**
 * Calculate overlap between two categories
 */
function calculateOverlap(cat1, cat2) {
  if (!cat1 || !cat2) return { hasSignificantOverlap: false }

  const xOverlap = Math.max(0, Math.min(cat1.maxX, cat2.maxX) - Math.max(cat1.minX, cat2.minX))
  const yOverlap = Math.max(0, Math.min(cat1.maxY, cat2.maxY) - Math.max(cat1.minY, cat2.minY))

  const cat1Area = (cat1.maxX - cat1.minX) * (cat1.maxY - cat1.minY)
  const cat2Area = (cat2.maxX - cat2.minX) * (cat2.maxY - cat2.minY)
  const overlapArea = xOverlap * yOverlap

  const overlapRatio = Math.min(cat1Area, cat2Area) > 0
    ? overlapArea / Math.min(cat1Area, cat2Area)
    : 0

  return {
    hasSignificantOverlap: overlapRatio > 0.3,
    overlapRatio
  }
}

/**
 * Format value for insight display
 */
function formatInsightValue(value, unit) {
  if (value == null || isNaN(value)) return 'N/A'

  let formatted
  if (value >= 1000) {
    formatted = value.toFixed(0)
  } else if (value >= 100) {
    formatted = value.toFixed(0)
  } else if (value >= 10) {
    formatted = value.toFixed(1)
  } else {
    formatted = value.toFixed(2)
  }

  return unit ? `${formatted} ${unit}` : formatted
}
